/**
 * What the page can tell us about its own runtime: a browser, or a web view
 * inside an installed app.
 *
 * The server also guesses this from the User-Agent, but conservatively — an app
 * shell that never touched the UA reads as `web` there, because the generic
 * in-app-web-view heuristics also match the Facebook and Instagram in-app
 * browsers, which are web traffic by any marketing definition. Here we are
 * inside the runtime and can check for the framework globals directly, so this
 * signal is the reliable one and the server prefers it.
 *
 * Everything is feature-detected off globals the native shells inject. Nothing
 * is imported: the SDK ships to every customer's site and must not gain a
 * dependency on Capacitor, Cordova, or React Native to answer this.
 */

/**
 * Re-exported, never redeclared. The server compares what we send here against
 * the same union, and two copies of a three-value type is exactly how the two
 * ends drift apart. Type-only, so nothing from @hamla/shared reaches the bundle
 * that ships to customers' sites.
 */
export type { AppSurface } from '@hamla/shared/campaigns';
import type { AppSurface } from '@hamla/shared/campaigns';

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  platform?: string;
}

interface AppGlobals {
  Capacitor?: CapacitorGlobal;
  cordova?: { platformId?: string };
  ReactNativeWebView?: unknown;
}

/** Map a framework's platform string onto our two app surfaces. */
function fromPlatform(platform: string | undefined): AppSurface | null {
  const value = (platform ?? '').toLowerCase();
  if (value === 'ios') return 'ios_app';
  if (value === 'android') return 'android_app';
  return null;
}

/** Last resort once we know it IS an app: which one, from the UA. */
function osFromUserAgent(): AppSurface {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios_app';
  if (/android/i.test(ua)) return 'android_app';
  // An app we cannot place is reported as web rather than guessed at — the same
  // rule the server follows, and for the same reason: a wrong app label hides
  // campaigns, a wrong web label at worst shows one.
  return 'web';
}

/**
 * Detect the current surface. Never throws — a probe that fails is answered
 * `web`, because reporting a surface is never worth breaking a page over.
 */
export function detectAppSurface(): AppSurface {
  if (typeof window === 'undefined') return 'web';

  try {
    const globals = window as unknown as AppGlobals;

    const capacitor = globals.Capacitor;
    if (capacitor?.isNativePlatform?.() === true) {
      const platform = capacitor.getPlatform?.() ?? capacitor.platform;
      return fromPlatform(platform) ?? osFromUserAgent();
    }

    const cordova = globals.cordova;
    if (cordova) return fromPlatform(cordova.platformId) ?? osFromUserAgent();

    if (globals.ReactNativeWebView) return osFromUserAgent();

    return 'web';
  } catch {
    return 'web';
  }
}
